import {
  AuthCurrentLoader,
  useAuth,
  useOnRefreshTokenExpired,
} from '@framework/auth';
import {
  getLocationState,
  Redirect,
  Route,
  useHistory,
  useModalHistoryCleanup,
  useScrollToTopOnMount,
  useSmoothAnchorScroll,
} from '@framework/ui';
import type { ILocationState } from '@framework/ui';
import AddressBookRoutes from '#build-flags/routes/address-book/Routes';
import DossierRoutes from '#build-flags/routes/dossiers/Routes';
import ExtendedUserLoginRoutes from '#build-flags/routes/user/ExtendedUserLoginRoutes';
import { FrontLoginRoutes } from '#theme';
import { Switch, useLocation } from 'react-router';
import { SharedResourcePage } from './shared-resource/SharedResourcePage';
import { NotFoundPage } from './errors/not-found/NotFoundPage';
import { InquiryCoverPage } from './inquiries/cover/InquiryCoverPage';
import { InquiryListPage } from './inquiries/list/InquiryListPage';
import { LoginPage } from './login/LoginPage';
import { ObservationPage } from './observations/detail/ObservationPage';
import { ObservationsListPage } from './observations/list/ObservationsListPage';
import { ArchiveListPage } from './submission/archive/list/ArchiveListPage';
import { ArchivePage } from './submission/archive/single/ArchivePage';
import { DelegationPage } from './submission/delegation/DelegationPage';
import { SubmissionListPage } from './submission/SubmissionListPage';
import { SubmissionPage } from './submission/SubmissionPage';
import { SubmissionResultPage } from './submission/SubmissionResultPage';
import { SubmissionSentPage } from './submission/SubmissionSentPage';
import { VerificationPage } from './verification/VerificationPage';

export const Routes = () => {
  const { isLogged, isPrelogged } = useAuth();

  const location = useLocation<ILocationState>();
  const { from } = getLocationState(location, '/domande');

  const history = useHistory();
  useOnRefreshTokenExpired(() => {
    history.push('/login', { ...(location.state ?? {}), from: location });
  });

  // Chiudiamo tutte le modal alla navigazione
  useModalHistoryCleanup(history);

  useSmoothAnchorScroll();
  useScrollToTopOnMount();

  if (isPrelogged && !isLogged) {
    // Autenticato ma profilo "per conto di" non ancora scelto: ogni rotta
    // viene convogliata alla schermata di scelta (resa da LoginPage).
    return (
      <AuthCurrentLoader>
        <Switch>
          {...FrontLoginRoutes}
          <Route path="/login" component={LoginPage} />
          <Redirect
            to={{
              pathname: '/login',
              state: { ...(location.state ?? {}), from: location },
            }}
          />
        </Switch>
      </AuthCurrentLoader>
    );
  }

  if (!isLogged) {
    return (
      <AuthCurrentLoader>
        <Switch>
          <Redirect exact from="/" to="/domande" />
          {/* Routes extra */}
          {...FrontLoginRoutes}

          <Route exact path="/domande" component={InquiryListPage} />
          <Route
            exact
            path="/categoria/:categoryId/domande/:id"
            component={InquiryCoverPage}
          />
          <Route
            exact
            path="/categoria/:categoryId"
            component={InquiryListPage}
          />
          <Route path="/domande/:id" component={InquiryCoverPage} />
          <Route path="/verifica" component={VerificationPage} />
          <Route exact path="/area-pubblica" component={ObservationsListPage} />
          <Route
            path="/area-pubblica/:id/moduli/:moduleId"
            component={ObservationPage}
          />
          <Route path="/area-pubblica/:id" component={ObservationPage} />
          {/* Pagina pubblica raggiunta via QR: accessibile anche da sloggati. */}
          <Route
            exact
            path="/risorsa-condivisa/:maskedId"
            component={SharedResourcePage}
          />
          <Route path="/login" component={LoginPage} />
          {process.env.NODE_ENV === 'development' && (
            <Route path={'/frontoffice/login' as any} component={LoginPage} />
          )}

          {ExtendedUserLoginRoutes && (
            <Route
              path={'/utente/' as any}
              render={() => <ExtendedUserLoginRoutes isLogged={isLogged} />}
            />
          )}

          <Redirect
            to={{
              pathname: '/login',
              state: { ...(location.state ?? {}), from: location },
            }}
          />
        </Switch>
      </AuthCurrentLoader>
    );
  }

  return (
    <AuthCurrentLoader>
      <Switch>
        <Redirect from="/login" to={from} />
        <Redirect exact from="/" to="/domande" />
        <Route path="/verifica" component={VerificationPage} />
        <Route
          exact
          path="/categoria/:categoryId/domande/:id"
          component={InquiryCoverPage}
        />
        <Route
          exact
          path="/categoria/:categoryId"
          component={InquiryListPage}
        />
        <Route path="/domande/:id" component={InquiryCoverPage} />
        <Route exact path="/domande" component={InquiryListPage} />

        <Route exact path="/area-pubblica" component={ObservationsListPage} />
        <Route
          path="/area-pubblica/:id/moduli/:moduleId"
          component={ObservationPage}
        />
        <Route path="/area-pubblica/:id" component={ObservationPage} />
        <Route
          exact
          path="/risorsa-condivisa/:maskedId"
          component={SharedResourcePage}
        />

        <Route exact path="/richieste/archiviate" component={ArchiveListPage} />
        <Route path="/richieste/archiviate/:id" component={ArchivePage} />

        <Route
          path="/richieste/:id/inviata/moduli/:moduleId"
          component={SubmissionSentPage}
        />

        <Route path="/richieste/:id/inviata" component={SubmissionSentPage} />
        <Route
          path="/richieste/:id/risultato"
          component={SubmissionResultPage}
        />
        <Route path="/richieste/:id/deleghe" component={DelegationPage} />

        {/* Non possiamo togliere questo path, altrimenti in SubmissionPage non si può accedere al moduleId dalle props */}
        {/* Inseriamo tutti i path possibili con gli ID per permettere al match di leggere le variabili dall'URL */}
        <Route
          path="/richieste/:id/moduli/:moduleId"
          component={SubmissionPage}
        />
        <Route path="/richieste/:id" component={SubmissionPage} />
        <Route exact path="/richieste" component={SubmissionListPage} />

        {DossierRoutes && <DossierRoutes />}

        {ExtendedUserLoginRoutes && (
          <Route
            path={'/utente/' as any}
            render={() => <ExtendedUserLoginRoutes isLogged={isLogged} />}
          />
        )}

        {AddressBookRoutes && <AddressBookRoutes />}

        {/** Pagina non trovata */}
        <Route component={NotFoundPage} />
      </Switch>
    </AuthCurrentLoader>
  );
};
